import type { Gradient } from '../../types/layout';

export type ShapeDividerType = 'wave' | 'zigzag' | 'curve' | 'bowl' | 'tilt';

export const SHAPE_DIVIDER_TYPES: ShapeDividerType[] = ['wave', 'zigzag', 'curve', 'bowl', 'tilt'];

export const SHAPE_DIVIDER_VIEWBOX = '0 0 1200 120';

const SVG_WIDTH = 1200;
const SVG_HEIGHT = 120;

function generateWavePath(frequency: number): string {
  const cycleWidth = SVG_WIDTH / frequency;
  let path = 'M0,0';
  for (let i = 0; i < frequency; i++) {
    const x0 = i * cycleWidth;
    const cp1x = x0 + cycleWidth / 4;
    const cp2x = x0 + (3 * cycleWidth) / 4;
    const x1 = x0 + cycleWidth;
    path += ` C${cp1x},${SVG_HEIGHT} ${cp2x},${SVG_HEIGHT} ${x1},0`;
  }
  path += ` L${SVG_WIDTH},0 L0,0 Z`;
  return path;
}

function generateZigzagPath(frequency: number): string {
  const peakWidth = SVG_WIDTH / frequency;
  let path = 'M0,0';
  for (let i = 0; i < frequency; i++) {
    const peakX = i * peakWidth + peakWidth / 2;
    const endX = (i + 1) * peakWidth;
    path += ` L${peakX},${SVG_HEIGHT} L${endX},0`;
  }
  path += ` L${SVG_WIDTH},0 L0,0 Z`;
  return path;
}

/**
 * Generates a single curve arc with the peak at `center` (0–100, percentage
 * of the width). The quadratic bezier control point is placed at the center
 * position so the curve peaks there.
 */
function generateCurvePath(center: number): string {
  const cpX = (SVG_WIDTH * center) / 100;
  return `M0,0 Q${cpX},${SVG_HEIGHT * 2} ${SVG_WIDTH},0 L${SVG_WIDTH},0 L0,0 Z`;
}

/**
 * Generates a concave arc (inverse of curve) with the lowest point at `center`
 * (0–100, percentage of the width). Fills the rectangle with a "scooped" shape.
 */
function generateBowlPath(center: number): string {
  const cpX = (SVG_WIDTH * center) / 100;
  return `M0,${SVG_HEIGHT} Q${cpX},${-SVG_HEIGHT} ${SVG_WIDTH},${SVG_HEIGHT} L${SVG_WIDTH},0 L0,0 Z`;
}

const TILT_PATH = `M0,0 L${SVG_WIDTH},${SVG_HEIGHT} L${SVG_WIDTH},0 L0,0 Z`;

export const SHAPE_DIVIDER_DEFAULTS: Record<ShapeDividerType, number> = {
  wave: 2,
  zigzag: 4,
  curve: 1,
  bowl: 1,
  tilt: 1,
};

export function getShapeDividerPath(
  type: ShapeDividerType,
  frequency: number,
  curveCenter?: number,
): string {
  switch (type) {
    case 'wave':
      return generateWavePath(frequency);
    case 'zigzag':
      return generateZigzagPath(frequency);
    case 'curve':
      return generateCurvePath(curveCenter ?? 50);
    case 'bowl':
      return generateBowlPath(curveCenter ?? 50);
    case 'tilt':
      return TILT_PATH;
  }
}

/** Static paths at default frequencies, used for shape thumbnails in the inspector. */
export const SHAPE_DIVIDER_DEFS: Record<ShapeDividerType, { viewBox: string; path: string }> = {
  wave: { viewBox: SHAPE_DIVIDER_VIEWBOX, path: generateWavePath(SHAPE_DIVIDER_DEFAULTS.wave) },
  zigzag: {
    viewBox: SHAPE_DIVIDER_VIEWBOX,
    path: generateZigzagPath(SHAPE_DIVIDER_DEFAULTS.zigzag),
  },
  curve: { viewBox: SHAPE_DIVIDER_VIEWBOX, path: generateCurvePath(50) },
  bowl: { viewBox: SHAPE_DIVIDER_VIEWBOX, path: generateBowlPath(50) },
  tilt: { viewBox: SHAPE_DIVIDER_VIEWBOX, path: TILT_PATH },
};

/**
 * Given a gradient and an edge of the element, returns the hex color of the
 * gradient stop that is closest to that edge.
 *
 * For linear gradients the CSS angle convention is used:
 *   0deg → to top (position 0 = bottom, position 1 = top)
 *   180deg → to bottom (position 0 = top, position 1 = bottom)
 *
 * For radial gradients the outermost stop is returned since edges are at the
 * perimeter.
 */
export function getGradientEdgeColor(gradient: Gradient, edge: 'top' | 'bottom'): string {
  if (!gradient.stops.length) return '';

  if (gradient.type === 'Linear') {
    const angle = gradient.angle || 0;
    // cos(0°)=1 → upward component, cos(180°)=-1 → downward component
    const vertComponent = Math.cos((angle * Math.PI) / 180);

    // When gradient goes upward (vert >= 0): position 1 = top, position 0 = bottom
    // When gradient goes downward (vert < 0): position 0 = top, position 1 = bottom
    const targetPosition =
      edge === 'top' ? (vertComponent >= 0 ? 1 : 0) : vertComponent >= 0 ? 0 : 1;

    const closest = gradient.stops.reduce((prev, curr) =>
      Math.abs(curr.position - targetPosition) < Math.abs(prev.position - targetPosition)
        ? curr
        : prev,
    );
    return closest.hexa;
  }

  // For radial gradients, the outermost stop is at the edges
  return gradient.stops[gradient.stops.length - 1].hexa;
}
